import { useNavigation } from '@react-navigation/native';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';

import {
  Button,
  ButtonsLayout,
  ButtonStyleEnum,
  Field,
  Form,
  FormButton,
  Separator,
  SettingsLayout,
} from '@components';
import { useAuthModel } from '@models/AuthModel';
import {
  ProfileNavigatorNavigationPropType,
  screens,
  ScreensNameType,
} from '@navigation';
import { ToastService } from '@services';
import { IS_WEB, parseApiError } from '@utils';
import {
  updatePasswordSchema,
  UpdatePasswordSchemaDataType,
} from '@validation';
import { useNavOptionsSet } from '@hooks';

const FormContainer = styled(View)({
  maxWidth: ['100%', 600],
  flexGrow: [1, 0],
  pt: ['$5', '$6'],
});

const cancelButtonStyle = IS_WEB
  ? ButtonStyleEnum.Outline
  : ButtonStyleEnum.Ghost;

const BREADCRUMB_ROUTES: ScreensNameType[] = [
  screens.Profile,
  screens.Settings,
  screens.Security,
  screens.UpdatePassword,
];

type NavigationPropType =
  ProfileNavigatorNavigationPropType<'UpdatePassword'>;

export const UpdatePasswordScreen = observer(() => {
  const { t } = useTranslation();
  const nav = useNavigation<NavigationPropType>();
  const authModel = useAuthModel();

  useNavOptionsSet({ title: t('updatePassword.title') });

  async function updatePassword(
    values: UpdatePasswordSchemaDataType,
  ) {
    try {
      await authModel.updatePassword.run({
        oldPassword: values.oldPassword,
        newPassword: values.password,
      });

      ToastService.showSuccess(t('updatePassword.successMessage'));

      closeUpdatePassword();
    } catch (e) {
      ToastService.showError(parseApiError(e).message);
    }
  }

  function closeUpdatePassword() {
    // We use navigate method here instead of goBack
    // to properly handle cancel button on web if page has been refreshed
    nav.navigate(screens.Security);
  }

  const initialValues: UpdatePasswordSchemaDataType = {
    oldPassword: '',
    password: '',
    passwordAgain: '',
  };

  return (
    <SettingsLayout
      title={t('updatePassword.title')}
      breadcrumbRoutes={BREADCRUMB_ROUTES}
    >
      <Form<UpdatePasswordSchemaDataType>
        initialValues={initialValues}
        onSubmit={updatePassword}
        validationSchema={updatePasswordSchema}
      >
        <FormContainer>
          <Field
            name="oldPassword"
            secureTextEntry
            placeholder={t('updatePassword.oldPassword')}
          />

          <Separator />
          <Field
            name="password"
            secureTextEntry
            placeholder={t('updatePassword.password')}
          />

          <Separator />
          <Field
            name="passwordAgain"
            secureTextEntry
            placeholder={t('updatePassword.confirmPassword')}
          />
          <Separator />
        </FormContainer>

        <ButtonsLayout>
          <FormButton>{t('updatePassword.change')}</FormButton>
          <Button
            buttonStyle={cancelButtonStyle}
            onPress={closeUpdatePassword}
          >
            {t('updatePassword.cancel')}
          </Button>
        </ButtonsLayout>
      </Form>
      <Separator size="$10" />
    </SettingsLayout>
  );
});
